import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reset successful!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to reset password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F9F8F6" }}>
        <div className="text-center">
          <h1 className="font-heading text-3xl text-[#1C1917] mb-4">Invalid Reset Link</h1>
          <p className="text-[#78716C] mb-8">This password reset link is invalid or has expired.</p>
          <a href="/admin" className="btn-primary inline-block">
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F9F8F6" }}>
        <div className="text-center">
          <CheckCircle size={64} className="mx-auto text-green-600 mb-6" />
          <h1 className="font-heading text-3xl text-[#1C1917] mb-4">Password Reset Complete</h1>
          <p className="text-[#78716C] mb-8">Your password has been successfully reset.</p>
          <button 
            onClick={() => navigate("/admin")}
            className="btn-primary"
            data-testid="go-to-login"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "#F9F8F6" }}>
      <div className="noise-overlay" />
      
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" 
            alt="Suite Lucina" 
            className="h-16 mx-auto mb-6"
          />
          <h1 className="font-heading text-3xl text-[#1C1917]">Reset Your Password</h1>
          <p className="text-[#78716C] mt-2">Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="input-luxury pr-12"
              data-testid="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <div>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="input-luxury"
              data-testid="confirm-password"
            />
          </div>
          <button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
            data-testid="reset-submit"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div className="mt-12 text-center">
          <a href="/admin" className="text-sm text-[#78716C] hover:text-[#1C1917] transition-colors">
            ← Back to Login
          </a>
        </div>
      </div>
    </div>
  );
}
